import React, { useLayoutEffect } from 'react';

import Meta from '../../components/meta.js';

const cssPrefix = 'case-study__';
const imgsrc = '/img/case-studies/cut-energy-bill-uk-case-study/';

const LandingCaseStudy = () => {
  useLayoutEffect(() => {
    const $root = document.querySelector('#root');
    $root.style.overflow = 'visible';

    return () => {
      $root.style.overflow = null;
    };
  }, []);

  return (
    <>
      <Meta
        title="How One Family Cut Their Energy Bill by £38/Month | Voltaware"
        description="Discover how Claire used Voltaware’s electricity usage monitor to cut her monthly energy bill by £38. A real UK success story in smart home energy saving."
        keywords="cut energy bill, electricity usage monitor, reduce electricity bill UK, smart energy monitor, Voltaware, home energy savings UK"
        image="https://voltaware.com/img/case-studies/cut-energy-bill-uk-case-study/not-alone.png"
        url="https://voltaware.com/"
      />

      <section className={`${cssPrefix}wrapper`}>
        <section>
          <h1 className={`${cssPrefix}h1`}>
            How Claire Used a Smart Electricity Usage Monitor to Cut Her Energy
            Bill by £38/Month
          </h1>

          <p className={`${cssPrefix}subtitle`}>
            Claire lives in a typical 3-bedroom semi in Manchester with her
            husband and two children. Like many families across the UK, she saw
            her{' '}
            <b>electricity bills soar in 2024 - up to around £135 a month.</b>
             Despite switching tariffs, she couldn’t seem to bring them down.
            Her smart meter showed how much electricity she was using, but it
            didn’t tell her what appliance is using it, or how to cut back.
          </p>

          <a className={`${cssPrefix}button`} href="/">
            Learn More About Voltaware
          </a>

          <div className={`${cssPrefix}not-alone-takeaway`}>
            <div>
              <p className={`${cssPrefix}title--inner`}>
                She's <span>not alone</span>
              </p>
              <p className={`${cssPrefix}text--medium`}>
                A national study of thousands of UK households, conducted by
                Cadent (the UK’s largest gas network, serving over 11 million
                customers) and Britain Thinks, revealed two shocking truths
                about how the British public feels about energy:
              </p>
              <img
                alt="voltaware cut energy bill UK"
                src={`${imgsrc}not-alone.png`}
              />

              <p className={`${cssPrefix}title--inner`}>
                The <span>takeaway?</span>
              </p>
              <p className={`${cssPrefix}text--medium`}>
                Millions are still looking for practical ways to cut energy
                costs and regain control.
              </p>
              <img
                alt="voltaware cut energy bill UK"
                className={`${cssPrefix}only--max-sm`}
                src={`${imgsrc}cut-energy-bill-UK.png`}
              />

              <div className={`${cssPrefix}not-alone-takeaway__item`}>
                <img
                  alt="voltaware cut energy bill UK"
                  src={`${imgsrc}takeaway-1.png`}
                />
                <p className={`${cssPrefix}text--medium`}>
                  <b>74% of consumers</b> feel anxious about the cost of their
                  energy bills.
                </p>
              </div>
              <div className={`${cssPrefix}not-alone-takeaway__item`}>
                <img
                  alt="voltaware cut energy bill UK"
                  src={`${imgsrc}takeaway-2.png`}
                />
                <p className={`${cssPrefix}text--medium`}>
                  <b>53% believe</b> there’s no point contacting their energy
                  supplier, as they don’t think any real help is available.
                </p>
              </div>
            </div>
            <img
              alt="voltaware cut energy bill UK"
              className={`${cssPrefix}img--sticky ${cssPrefix}only--min-sm`}
              src={`${imgsrc}cut-energy-bill-UK.png`}
            />
          </div>

          <div className={`${cssPrefix}challenge-solution`}>
            <div
              className={`${cssPrefix}challenge-solution__img-wrapper ${cssPrefix}img--sticky`}
            >
              <img
                alt="voltaware sensor"
                src={`${imgsrc}voltaware-sensor.png`}
              />
            </div>
            <div>
              <div className={`${cssPrefix}challenge`}>
                <h2 className={`${cssPrefix}h2`}>The Challenge</h2>
                <p>
                  Claire, like so many other homeowners, found herself asking
                  the same question:{' '}
                  <b>
                    How can I cut my energy bill without turning off the heating
                    or sitting in the dark?
                  </b>
                </p>
                <p>
                  Traditional smart meters provide total consumption data, but
                  they don’t show which appliances are responsible for driving
                  up the cost. Without that information, it’s{' '}
                  <b>difficult to take effective action.</b>
                </p>
                <p>
                  <b>Claire wanted a smarter solution</b> - something that would
                  give her detailed insights, recommend impactful changes to
                  ultimately help her reduce her electricity usage in a way that
                  genuinely made a difference to her monthly bill.
                </p>
              </div>
              <div className={`${cssPrefix}solution`}>
                <h2 className={`${cssPrefix}h2`}>The Solution</h2>
                <p>
                  <b>Claire discovered Voltaware</b>, a smart electricity usage
                  monitor designed to break down a home’s energy consumption by
                  appliance. Unlike standard smart meters, Voltaware offers ive
                  data on individual devices. Once installed and connected to
                  the Voltaware app, the system begins learning the unique
                  energy footprint of different appliances in the home.
                </p>
                <p>
                  Claire had the Voltaware monitor installed in her fuse box and
                  downloaded the app. Whilst it takes roughly a month to learn
                  each appliance’s unique signature, she was able to get a good
                  estimate of her appliances’ energy use within 24 hours.,
                  detailing what appliances were using the most electricity,
                  what appliances ran during expensive, peak times, and which
                  energy habits were costing her the most money. The electricity
                  usage monitor helped her understand not just how much energy
                  was being used, but where it was being used.
                </p>
                <div className={`${cssPrefix}solution__insights`}>
                  <div>
                    <p>
                      <strong>2000+</strong>
                    </p>
                    <p>Monthly Insights</p>
                  </div>
                  <div>
                    <p>
                      <strong>$5 million</strong>
                    </p>
                    <p>Economy Generated</p>
                  </div>
                </div>
                <a className={`${cssPrefix}button`} href="/">
                  Learn More About Voltaware
                </a>
              </div>
            </div>
          </div>

          <div className={`${cssPrefix}what-changed`}>
            <h2 className={`${cssPrefix}h2`}>
              What Changed:
              <span className={`${cssPrefix}title--inner`}>
                How Claire Cut Her Energy Bill
              </span>
            </h2>

            <p className={`${cssPrefix}text--medium`}>
              Over the next few weeks, Claire made a few small adjustments that
              added up to a big monthly saving:
            </p>

            <div
              className={`${cssPrefix}what-changed__img-wrapper ${cssPrefix}only--max-sm`}
            >
              <img
                alt="voltaware ios and android app"
                src={`${imgsrc}voltaware-app.png`}
              />
            </div>

            <div className={`${cssPrefix}what-changed__wrapper`}>
              <div>
                <div className={`${cssPrefix}what-changed__item`}>
                  <span>
                    <img
                      alt="icon wallet opened"
                      src={`${imgsrc}ico-wallet-add.svg`}
                    />
                  </span>
                  <div>
                    <h4 className={`${cssPrefix}h4`}>Electrical Heating:</h4>
                    <p>
                      One of the first things she noticed was that the
                      underfloor heating in the bathroom was running overnight -
                      something she hadn’t realised. This alone was costing her
                      roughly <b>£18 per month</b>. She adjusted the timer and
                      began using it more selectively.
                    </p>
                  </div>
                </div>

                <div className={`${cssPrefix}what-changed__item`}>
                  <span>
                    <img alt="icon wallet" src={`${imgsrc}ico-wallet.svg`} />
                  </span>
                  <div>
                    <h4 className={`${cssPrefix}h4`}>Laundry & Dishwasher:</h4>
                    <p>
                      Voltaware revealed that the washing machine and dishwasher
                      were running during peak daytime hours, when electricity
                      rates were higher. Claire switched these to run overnight
                      or during off-peak periods, saving around{' '}
                      <b>£10 per month</b>.
                    </p>
                  </div>
                </div>

                <div className={`${cssPrefix}what-changed__item`}>
                  <span>
                    <img alt="icon wallet" src={`${imgsrc}ico-money-4.svg`} />
                  </span>
                  <div>
                    <h4 className={`${cssPrefix}h4`}>Standby Devices:</h4>
                    <p>
                      TVs, monitors, and other electronics were left on standby
                      throughout the night. By unplugging these or using smart
                      plugs with timers, she saved another <b>£5 per month</b>.
                    </p>
                  </div>
                </div>

                <div className={`${cssPrefix}what-changed__item`}>
                  <span>
                    <img alt="icon wallet" src={`${imgsrc}ico-safe-home.svg`} />
                  </span>
                  <div>
                    <h4 className={`${cssPrefix}h4`}>Energy Alerts:</h4>
                    <p>
                      Claire set up usage alerts within the app, which notified
                      her of any unusual spikes in electricity use - giving her
                      the chance to take action before the next bill arrived.
                    </p>
                  </div>
                </div>
              </div>
              <div
                className={`${cssPrefix}what-changed__img-wrapper ${cssPrefix}img--sticky ${cssPrefix}only--min-sm`}
              >
                <img
                  alt="voltaware ios and android app"
                  src={`${imgsrc}voltaware-app.png`}
                />
              </div>
            </div>
          </div>

          <div className={`${cssPrefix}results`}>
            <h2 className={`${cssPrefix}h2`}>The Results</h2>

            <p className={`${cssPrefix}text--medium`}>
              Thanks to Voltaware’s smart electricity usage monitor, Claire cut
              her monthly electricity bill by <b>£38</b>, reducing it from{' '}
              <strong>£135 to around £97</strong>. That’s a{' '}
              <strong>28% monthly saving</strong> - without compromising on home
              comfort or convenience.
            </p>
          </div>

          <div className={`${cssPrefix}bigger-picture`}>
            <h2 className={`${cssPrefix}h2`}>The Bigger Picture</h2>

            <p className={`${cssPrefix}text--medium`}>
              Claire’s story is just one example of how UK households are using
              technology to take control of rising energy costs. In a time when
              energy prices remain volatile and millions of households are
              struggling with the cost of living, tools like Voltaware are
              becoming essential.
            </p>
            <p className={`${cssPrefix}text--medium`}>
              Many people in the UK still feel that they don’t have the
              information or support needed to make confident decisions about
              their electricity usage. Smart meters are a step in the right
              direction, but they only go so far. A dedicated{' '}
              <strong>electricity usage monitor</strong> like Voltaware fills
              the gap by giving families the appliance-level detail they need to
              cut waste and reduce energy costs.
            </p>
          </div>

          <div className={`${cssPrefix}cadent-report`}>
            <div>
              <p className={`${cssPrefix}text--medium`}>
                The same Cadent report revealed that:
              </p>
              <div className={`${cssPrefix}cadent-report__grid`}>
                <img
                  alt="voltaware cut energy bill UK"
                  src={`${imgsrc}cadent-report-1.png`}
                />
                <p className={`${cssPrefix}text--medium`}>
                  Only <b>24% of people</b> believe their supplier offers
                  financial support.
                </p>
                <img
                  alt="voltaware cut energy bill UK"
                  src={`${imgsrc}cadent-report-2.png`}
                />
                <p className={`${cssPrefix}text--medium`}>
                  Just <b>14% think</b> the Government is doing enough to
                  support households with energy bills.
                </p>
              </div>
              <p className={`${cssPrefix}text--medium`}>
                With stats like these, it’s clear that the British public are
                looking for ways to help themselves - and Voltaware offers a
                practical, data-driven solution.
              </p>
            </div>
            <img
              alt="voltaware cut energy bill UK"
              className={`${cssPrefix}img--sticky`}
              src={`${imgsrc}savings-potential.png`}
            />
          </div>

          <div className={`${cssPrefix}verdict`}>
            <img
              alt="voltaware cut energy bill UK"
              className={`${cssPrefix}img--sticky`}
              src={`${imgsrc}energy-efficiency.png`}
            />
            <div>
              <div className={`${cssPrefix}verdict__item`}>
                <h2 className={`${cssPrefix}h2`}>Claire's Verdict</h2>
                <blockquote>
                  “I couldn’t believe how much electricity we were wasting. Once
                  I could see my energy usage broken down by appliance, it was
                  so much easier to make changes. Voltaware gave me real
                  visibility - and now I feel in control of my bills for the
                  first time.”
                </blockquote>
              </div>
              <div className={`${cssPrefix}verdict__item`}>
                <h2 className={`${cssPrefix}h2`}>Why Voltaware?</h2>
                <p>
                  Voltaware isn’t just a gadget - it’s a smart, affordable tool
                  that empowers households to take control of their energy
                  usage. It helps families like Claire’s cut their energy bills
                  by providing:
                </p>
                <ul>
                  <li>Real-time appliance-level energy monitoring</li>
                  <li>Actionable insights and cost-saving recommendations</li>
                  <li>Custom alerts for abnormal usage</li>
                  <li>Simple app interface, accessible for all ages</li>
                </ul>
                <p>
                  Whether you want to lower your electricity bill, identify
                  hidden energy drains, or just feel more in control of your
                  home’s energy usage, Voltaware is a powerful ally.
                </p>
              </div>
            </div>
          </div>

          <div className={`${cssPrefix}looking-to-cut`}>
            <h3 className={`${cssPrefix}h2`}>
              Looking to cut your energy bill too?
            </h3>

            <p className={`${cssPrefix}text--medium`}>
              Tens of thousands of UK households are already saving with
              Voltaware - no complex installs, no lifestyle changes, just real
              results.
            </p>
          </div>

          <a
            className={`${cssPrefix}button--orange`}
            href="/amazon-shop"
            rel="noreferrer noopener"
            target="_blank"
          >
            See How Voltaware Works
          </a>
        </section>
      </section>
    </>
  );
};

export default LandingCaseStudy;
